import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const FAQ_ITEMS = [
  { q: 'When is a Waste Management Plan required for a DA?', a: 'Most NSW and VIC councils require a Waste Management Plan as part of any Development Application for residential developments of 3+ dwellings, commercial developments, mixed-use buildings, and any project involving demolition.' },
  { q: 'How much does a Waste Management Plan cost?', a: 'Operational WMPs typically range from $1,500 to $5,000+ depending on project complexity, number of dwellings, and council requirements. Green Star and SSDA projects are at the higher end.' },
  { q: 'What is the difference between an Operational WMP and a D&CWMP?', a: 'An Operational WMP covers ongoing waste management for the completed building. A Demolition & Construction WMP covers the construction phase including demolition waste volumes and material recovery targets.' },
  { q: 'Do you handle Green Star waste credits?', a: 'Yes. We have extensive experience with Green Star Design & As-Built v1.3 Credit 7A, Credit 8A, and Credit 22B. We prepare specialist plans for GBCA submission.' },
  { q: 'How long does it take to prepare a WMP?', a: 'Standard Operational WMPs are typically delivered within 5 business days. Complex projects (SSDA, Green Star) may take 2-3 weeks.' },
  { q: 'Can you help if council has rejected our current WMP?', a: 'Yes. We regularly assist with WMP revisions following council feedback, including Section 4.55 modifications.' },
];

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQ_ITEMS.map(f => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a }
  }))
});

const SERVICES = [
  {
    id: 'opwmp',
    name: 'Operational Waste Management Plans',
    abbr: 'OpWMP',
    color: '#3b82f6',
    bgColor: '#eff6ff',
    borderColor: '#bfdbfe',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
    description: 'Comprehensive operational waste management plans for residential, commercial, and mixed-use developments. Our OpWMPs address ongoing waste generation, storage, collection, and disposal requirements — tailored to your specific council\'s DCP controls.',
    deliverables: [
      'Waste generation volume calculations',
      'Bin configuration and storage area design',
      'Collection vehicle access assessment',
      'Waste stream separation strategies',
      'Council-specific compliance documentation',
      'Waste signage and education recommendations',
    ],
    sectors: ['Residential apartments', 'Commercial offices', 'Mixed-use developments', 'Retail centres', 'Industrial facilities', 'Aged care and hospitals'],
  },
  {
    id: 'dcwmp',
    name: 'Demolition & Construction Waste Management Plans',
    abbr: 'D&CWMP',
    color: '#f59e0b',
    bgColor: '#fffbeb',
    borderColor: '#fde68a',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    description: 'Demolition and construction waste management plans that quantify waste volumes, set material recovery targets, and detail disposal logistics. Essential for DA submissions where councils require evidence of responsible demolition and construction waste handling.',
    deliverables: [
      'Demolition waste volume estimates',
      'Material recovery and recycling targets (typically 80%+)',
      'Licensed waste facility identification',
      'On-site waste segregation strategies',
      'Hazardous material handling procedures',
      'Waste tracking and reporting framework',
    ],
    sectors: ['New builds', 'Demolition projects', 'Renovation and refurbishment', 'Heritage buildings', 'Large-scale infrastructure', 'Government projects'],
  },
  {
    id: 'swmp',
    name: 'Stormwater Management Plans',
    abbr: 'SWMP',
    color: '#6366f1',
    bgColor: '#eef2ff',
    borderColor: '#c7d2fe',
    icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z',
    description: 'Stormwater quality and quantity management strategies including Water Sensitive Urban Design (WSUD) responses, erosion and sediment control plans, and stormwater treatment train design. Required by most NSW and VIC councils for developments that increase impervious surfaces.',
    deliverables: [
      'WSUD strategy and compliance assessment',
      'Stormwater quality modelling (MUSIC)',
      'Erosion and sediment control plans',
      'On-site detention (OSD) sizing',
      'Treatment train design and specification',
      'Maintenance schedules for stormwater assets',
    ],
    sectors: ['Multi-unit residential', 'Commercial developments', 'Industrial estates', 'Mixed-use precincts', 'Greenfield subdivisions', 'Urban infill projects'],
  },
  {
    id: 'cmp',
    name: 'Construction Management Plans',
    abbr: 'CMP',
    color: '#10b981',
    bgColor: '#ecfdf5',
    borderColor: '#a7f3d0',
    icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064',
    description: 'Site-specific construction management plans covering waste handling, traffic management, noise control, dust suppression, and environmental protection measures. These plans demonstrate to councils how construction-phase impacts will be managed responsibly.',
    deliverables: [
      'Construction traffic management plan',
      'Noise and vibration management',
      'Dust and air quality controls',
      'Construction waste logistics',
      'Environmental protection measures',
      'Community notification procedures',
    ],
    sectors: ['Urban construction sites', 'Road and infrastructure works', 'Sensitive receiver environments', 'Heritage-adjacent sites', 'Hospital and school precincts', 'CBD and high-density areas'],
  },
];

const ADDITIONAL = [
  {
    name: 'HAZMAT Surveys & Reports',
    desc: 'Pre-demolition hazardous materials surveys identifying asbestos, lead paint, synthetic mineral fibres, and PCBs. Division 5 and Division 6 HAZMAT reports for WorkCover compliance.',
    icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  },
  {
    name: 'Green Star Waste Credits',
    desc: 'Specialist documentation for Green Star Design & As-Built v1.3 Credit 7A (Operational Waste), Credit 8A (Construction & Demolition Waste), and Credit 22B (Waste Management).',
    icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
  },
  {
    name: 'Waste Audits',
    desc: 'Comprehensive waste audits for existing facilities — identifying waste streams, contamination rates, and opportunities to improve diversion rates and reduce costs.',
    icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
];

export default function Services() {
  return (
    <PublicLayout>
      <Helmet>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SEO
        title="Services — Waste Management Plan Consultants"
        description="Operational WMPs, Demolition & Construction WMPs, Stormwater Management Plans, CMPs, HAZMAT surveys, and Green Star waste credits. Specialist consulting for NSW & VIC developments."
        keywords="waste management plan services, operational WMP, demolition construction WMP, stormwater management plan, HAZMAT survey, Green Star waste credit, construction management plan, waste audit"
        canonicalPath="/services"
      />

      {/* Hero */}
      <section className="py-16 lg:py-20 bg-[#F8FAFC]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">OUR SERVICES</div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-[#0A2540] leading-tight mb-4" data-testid="services-headline">
              Waste &amp; Environmental Planning Services
            </h1>
            <p className="text-base text-slate-600 leading-relaxed">
              We provide specialist waste management consulting services across NSW and VIC — from standard DA submissions to complex Green Star and SSDA projects. Every plan is written by an experienced consultant, not auto-generated.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-16 bg-white" data-testid="core-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-16">
          {SERVICES.map((service, idx) => (
            <div key={service.id} id={service.id} className={`grid lg:grid-cols-2 gap-10 items-start ${idx % 2 === 1 ? 'lg:direction-rtl' : ''}`} data-testid={`service-${service.id}`}>
              <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: service.bgColor }}>
                    <svg className="w-5 h-5" style={{ color: service.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={service.icon} />
                    </svg>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: service.color }}>{service.abbr}</span>
                </div>
                <h2 className="text-2xl font-bold text-[#0A2540] mb-3">{service.name}</h2>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{service.description}</p>
                <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-medium transition-colors shadow-md" style={{ backgroundColor: service.color }}>
                  Request a Quote
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              </div>
              <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6">
                  <h3 className="text-sm font-bold text-[#0A2540] mb-4">Key Deliverables</h3>
                  <ul className="space-y-2.5">
                    {service.deliverables.map((d, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: service.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {d}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Sectors</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {service.sectors.map((s, i) => (
                        <span key={i} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="additional-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">ALSO AVAILABLE</div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0A2540]">Additional Services</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {ADDITIONAL.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_8px_rgb(0,0,0,0.02)]" data-testid={`additional-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-[#0A2540] mb-2">{item.name}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white" data-testid="services-faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">FREQUENTLY ASKED QUESTIONS</div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0A2540]">Common questions about Waste Management Plans</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'When is a Waste Management Plan required for a DA?', a: 'Most NSW and VIC councils require a Waste Management Plan as part of any Development Application for residential developments of 3+ dwellings, commercial developments, mixed-use buildings, and any project involving demolition. Some councils require them for even smaller projects. We can advise whether your specific council and project type requires one.' },
              { q: 'How much does a Waste Management Plan cost?', a: 'Operational WMPs typically range from $1,500 to $5,000+ depending on project complexity, number of dwellings, and council requirements. Green Star and SSDA projects are at the higher end due to additional specialist documentation. We provide tailored quotes based on your specific project details.' },
              { q: 'What is the difference between an Operational WMP and a D&CWMP?', a: 'An Operational WMP covers ongoing waste management for the completed building — bin sizes, collection schedules, storage areas, and waste streams. A Demolition & Construction WMP covers the construction phase — demolition waste volumes, material recovery targets, on-site waste handling, and licensed disposal facilities. Many DAs require both.' },
              { q: 'Do you handle Green Star waste credits?', a: 'Yes. We have extensive experience with Green Star Design & As-Built v1.3 Credit 7A (Operational Waste), Credit 8A (Construction & Demolition Waste), and Credit 22B (Waste Management). We prepare the specialist plans required for GBCA submission and can liaise with your Green Star assessor.' },
              { q: 'How long does it take to prepare a WMP?', a: 'Standard Operational WMPs are typically delivered within 5 business days of receiving architectural plans and project details. Complex projects (SSDA, Green Star, multi-stage developments) may take 2-3 weeks depending on scope. We provide clear timelines in our fee proposals.' },
              { q: 'Can you help if council has rejected our current WMP?', a: 'Absolutely. We regularly assist with WMP revisions following council feedback, including Section 4.55 modifications. We understand what each council is looking for and can identify the specific gaps that need addressing. Send us the council feedback and we will provide a quote to resolve the issues.' },
            ].map((faq, i) => (
              <details key={i} className="group bg-slate-50 rounded-xl border border-slate-100 overflow-hidden" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                  <span className="text-sm font-semibold text-[#0A2540] pr-4">{faq.q}</span>
                  <svg className="w-4 h-4 text-slate-400 flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </summary>
                <div className="px-6 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A2540]" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 tracking-tight">Not sure which service you need?</h2>
          <p className="text-slate-400 text-base mb-8 max-w-lg mx-auto">Send us your plans and project details &mdash; we&apos;ll advise which reports are required for your specific council and application type.</p>
          <Link to="/contact" className="inline-flex px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg" data-testid="services-cta-btn">
            Get in Touch
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
